import { useModalTrap } from '~/shared/ui/focus-trap';
import { For, Show, createMemo, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { X } from 'lucide-solid';
import { DOCUMENTED_SHORTCUTS, type DocumentedShortcut } from '~/shared/lib/shortcuts';
import { useI18n } from '~/shared/lib/i18n';

interface Props {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_LABEL: Record<DocumentedShortcut['category'], string> = {
  global: 'Глобальные',
  navigation: 'Навигация',
  schedule: 'Шахматка',
  forms: 'Формы',
};

export const ShortcutsDialog: Component<Props> = (props) => {
  const { t } = useI18n();
  const grouped = createMemo(() => {
    const map = new Map<DocumentedShortcut['category'], DocumentedShortcut[]>();
    for (const s of DOCUMENTED_SHORTCUTS) {
      const bucket = map.get(s.category) ?? [];
      bucket.push(s);
      map.set(s.category, bucket);
    }
    return [...map.entries()];
  });

  return (
    <Show when={props.open}>
      <Portal>
        <div
          ref={useModalTrap}
          class="fixed inset-0 z-[1050] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={t('shortcuts.dialogTitle')}
        >
          <button
            type="button"
            aria-label={t('common.close')}
            class="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={props.onClose}
          />
          <div class="glass-strong relative w-full max-w-[560px] max-h-[80vh] flex flex-col overflow-hidden animate-dialog-in">
            <header class="flex items-center justify-between h-14 px-5 border-b border-hairline">
              <h2 class="text-[17px] font-semibold">{t('shortcuts.dialogTitle')}</h2>
              <button
                type="button"
                class="press p-1.5 rounded-sm hover:bg-white/8"
                onClick={props.onClose}
                aria-label={t('common.close')}
              >
                <X class="h-4 w-4" />
              </button>
            </header>

            <div class="flex-1 overflow-y-auto p-5 space-y-5">
              <For each={grouped()}>
                {([category, items]) => (
                  <section>
                    <h3 class="text-[11px] uppercase tracking-wider text-tertiary mb-2">
                      {CATEGORY_LABEL[category]}
                    </h3>
                    <ul class="divide-y divide-hairline">
                      <For each={items}>
                        {(shortcut) => (
                          <li class="flex items-center justify-between py-2.5">
                            <span class="text-[14px] text-primary">
                              {shortcut.description}
                            </span>
                            <kbd class="tabular text-[12px] font-medium px-2 py-1 rounded-sm bg-white/8 border border-hairline text-secondary">
                              {shortcut.combo}
                            </kbd>
                          </li>
                        )}
                      </For>
                    </ul>
                  </section>
                )}
              </For>
            </div>
          </div>

          <style>{`
            @keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }
            @keyframes dialog-in {
              from { transform: translateY(8px) scale(0.98); opacity: 0 }
              to   { transform: translateY(0) scale(1);      opacity: 1 }
            }
            .animate-fade-in    { animation: fade-in   var(--duration-base) var(--ease-ios) both; }
            .animate-dialog-in  { animation: dialog-in var(--duration-base) var(--ease-ios) both; }
          `}</style>
        </div>
      </Portal>
    </Show>
  );
};
